import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowRight, faSignOutAlt, faLock} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../../utils/utils';
import {logoutModernAuth} from '../../utils/sessionAuth';
import {DASHBOARD_REGISTRY, DashboardId, TIERS} from '../../config/dashboardRegistry';

/**
 * Post-login landing page (dashboard_architecture_plan.md, section B2),
 * modelled on farmigram's pages/auth/DashboardSelector.tsx. A principal with
 * exactly one accessible dashboard skips straight through rather than
 * showing a one-card selector - there's no decision to make in that case.
 */

type ApiDashboard = { id: DashboardId; label: string; is_internal: boolean };
type TierInfo = {
    tier: string; tier_label: string; max_dashboards: number;
    dashboards_used: number; available_tiers: { id: string; label: string }[];
};

const getStoredToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';

const DashboardSelector: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [dashboards, setDashboards] = useState<ApiDashboard[]>([]);
    const [tier, setTier] = useState<TierInfo | null>(null);
    const [displayName, setDisplayName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = 'Choose your dashboard - Uzima Healthcare';
        const token = getStoredToken();
        if (!token) {
            navigate('/institution/newlog');
            return;
        }

        axios.get(`${baseUrl}platform/my-dashboards/`, {
            headers: {Authorization: `Bearer ${token}`},
        }).then((res) => {
            const data = res.data?.data;
            if (!data) throw new Error('Malformed response');
            setDisplayName(data.display_name || '');
            setTier(data.tier || null);

            const accessible: ApiDashboard[] = data.dashboards || [];
            if (accessible.length === 1) {
                // Nothing to choose - go straight in.
                const meta = DASHBOARD_REGISTRY[accessible[0].id];
                if (meta) {
                    navigate(meta.basePath, {replace: true});
                    return;
                }
            }
            setDashboards(accessible);
            setLoading(false);
        }).catch(() => {
            setError('Could not load your dashboards. Please sign in again.');
            setLoading(false);
        });
    }, [baseUrl, navigate]);

    const enterDashboard = (id: DashboardId) => {
        const meta = DASHBOARD_REGISTRY[id];
        if (meta) navigate(meta.basePath);
    };

    const handleLogout = async () => {
        await logoutModernAuth(baseUrl);
        navigate('/institution/newlog');
    };

    if (loading) {
        return (
            <div className="min-h-screen d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen d-flex flex-column align-items-center justify-content-center gap-3">
                <p className="text-danger">{error}</p>
                <button className="btn btn-primary" onClick={() => navigate('/institution/newlog')}>
                    Back to Sign In
                </button>
            </div>
        );
    }

    const customerDashboards = dashboards.filter((d) => !d.is_internal);
    const internalDashboards = dashboards.filter((d) => d.is_internal);

    return (
        <div style={{minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #ecfdf9 100%)'}}>
            <div className="container py-5">
                <div className="d-flex justify-content-between align-items-start mb-5">
                    <div>
                        <h1 className="fw-bold mb-1">Welcome{displayName ? `, ${displayName}` : ''}</h1>
                        <p className="text-muted mb-0">Choose a dashboard to continue</p>
                    </div>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} className="me-2"/>
                        Sign out
                    </button>
                </div>

                {dashboards.length === 0 && (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="mb-0">No dashboards are available on your account yet.</p>
                        </div>
                    </div>
                )}

                {customerDashboards.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted small fw-bold mb-3">Your Dashboards</h6>
                        <div className="row mb-5">
                            {customerDashboards.map((d) => {
                                const meta = DASHBOARD_REGISTRY[d.id];
                                if (!meta) return null;
                                return (
                                    <div key={d.id} className="col-md-6 col-lg-4 mb-4">
                                        <div
                                            className="card card-animate h-100"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => enterDashboard(d.id)}
                                        >
                                            <div className="card-body d-flex flex-column">
                                                <div className="uz-stat-icon mb-3" style={{backgroundColor: 'rgba(10,179,156,.12)', color: '#0ab39c'}}>
                                                    <FontAwesomeIcon icon={meta.icon}/>
                                                </div>
                                                <h5 className="card-title">{meta.label}</h5>
                                                <p className="text-muted small flex-grow-1">{meta.description}</p>
                                                <span className="text-primary small fw-semibold">
                                                    Enter <FontAwesomeIcon icon={faArrowRight} className="ms-1"/>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {internalDashboards.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted small fw-bold mb-3">Internal Dashboards</h6>
                        <div className="row mb-5">
                            {internalDashboards.map((d) => {
                                const meta = DASHBOARD_REGISTRY[d.id];
                                if (!meta) return null;
                                return (
                                    <div key={d.id} className="col-md-6 col-lg-4 mb-4">
                                        <div
                                            className="card card-animate h-100"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => enterDashboard(d.id)}
                                        >
                                            <div className="card-body d-flex flex-column">
                                                <div className="uz-stat-icon mb-3" style={{backgroundColor: 'rgba(64,81,137,.12)', color: '#405189'}}>
                                                    <FontAwesomeIcon icon={meta.icon}/>
                                                </div>
                                                <h5 className="card-title">{meta.label}</h5>
                                                <p className="text-muted small flex-grow-1">{meta.description}</p>
                                                <span className="text-primary small fw-semibold">
                                                    Enter <FontAwesomeIcon icon={faArrowRight} className="ms-1"/>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {tier && (
                    <div className="card">
                        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <FontAwesomeIcon icon={faLock} className="me-2 text-muted"/>
                                <strong>{tier.tier_label} plan</strong>
                                <span className="text-muted ms-2">
                                    {tier.dashboards_used} of {tier.max_dashboards === -1 ? 'unlimited' : tier.max_dashboards} dashboard{tier.max_dashboards === 1 ? '' : 's'} used
                                </span>
                            </div>
                            {tier.max_dashboards !== -1 && tier.dashboards_used >= tier.max_dashboards && (
                                <span className="badge bg-soft-primary text-primary">Upgrade to add more dashboards</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardSelector;
